/**
 * Typed API client for the automations backend.
 *
 * Shape mirrors apps/web/src/lib/api.ts (bearer-token attachment, {data}
 * unwrapping, {error:{code,message}} mapping) but simplified to this app's
 * needs: no upload/SSE helpers yet.
 *
 * Several routes below are not implemented server-side yet and currently
 * return 501. Rather than let that surface as a raw network failure, every
 * wrapper below is designed so a 501 is a first-class, expected outcome —
 * callers check `err.status === 501` and render a "coming soon" state (see
 * ClientDetailPage, HealthPage, DemoPage).
 */
import { auth } from './firebase';
import type { InboundAddress,
  Client,
  ClientDetail,
  ClientMe,
  CreateClientInput,
  DemoBlock,
  DemoRun,
  DeployBlockInput,
  Deployment,
  Block,
  HealthReport,
  LedgerDelivery,
  LedgerEventWithClient,
  LedgerThread,
  OutcomesSummary,
  OverviewRow,
  StartDemoRunInput,
  UpdateClientInput,
} from './types';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://automations-api.hattieknows.com';

export interface ApiErrorShape {
  code: string;
  message: string;
  status: number;
}

/** Thrown by apiFetch for any non-2xx response. */
export class ApiError extends Error implements ApiErrorShape {
  code: string;
  status: number;
  /**
   * Per-field problems from a 422. The server re-validates every config on
   * deploy — it is the authoritative check, since the browser's copy of a
   * block's field definitions can be stale — so these need to reach the form
   * and mark the offending fields, not just become a toast.
   */
  issues?: { key: string; message: string }[] | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    issues?: { key: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

/**
 * Thrown by publicApiFetch instead of ApiError, so callers (and error
 * boundaries) can tell an unauthenticated-by-design demo call apart from an
 * authenticated one that failed — a public 401/403 never means "sign in
 * again", so the UI should never suggest that.
 */
export class PublicApiError extends ApiError {
  constructor(status: number, code: string, message: string) {
    super(status, code, message);
    this.name = 'PublicApiError';
  }
}

interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
    issues?: { key: string; message: string }[];
  };
}

async function parseErrorBody(response: Response): Promise<{
  code: string;
  message: string;
  issues?: { key: string; message: string }[];
}> {
  let code = 'UNKNOWN_ERROR';
  let message = `HTTP ${response.status}`;
  let issues: { key: string; message: string }[] | undefined;
  try {
    const body = (await response.json()) as ErrorBody;
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      if (Array.isArray(body.error.issues)) issues = body.error.issues;
    }
  } catch {
    // Body wasn't JSON (or was empty) — fall back to the defaults above.
  }
  return { code, message, ...(issues ? { issues } : {}) };
}

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

interface UnwrappedBody<T> {
  data: T;
}

/** Authenticated fetch. Attaches a bearer token when signed in, unwraps `{data}`, throws ApiError on failure. */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    const { code, message, issues } = await parseErrorBody(response);
    throw new ApiError(response.status, code, message, issues);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as UnwrappedBody<T>;
  return body.data;
}

/** Unauthenticated fetch for public/demo endpoints — never attaches a token, throws PublicApiError on failure. */
export async function publicApiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    const { code, message } = await parseErrorBody(response);
    throw new PublicApiError(response.status, code, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as UnwrappedBody<T>;
  return body.data;
}

/**
 * Builds a `?a=b&c=d` query string from a params object, skipping any key
 * whose value is `undefined` (an omitted filter, not an empty one). Values
 * are stringified and `encodeURIComponent`-escaped; keys here are always
 * static string literals so they don't need escaping.
 */
function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/** True when an ApiError represents a route that exists but isn't built yet. */
export function isNotImplemented(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 501;
}

/** True when an ApiError represents a rate limit (e.g. the demo's daily cap). */
export function isRateLimited(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 429;
}

/**
 * True when an ApiError represents the demo being unavailable right now
 * (no provisioned block, engine unreachable) rather than a fault with the
 * visitor's input — the API reports this as 404 or 503 with a
 * DEMO_NOT_AVAILABLE code, and the UI treats it the same as "coming soon".
 */
export function isNotAvailable(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 404 || err.status === 503);
}

export const adminApi = {
  listClients: () => apiFetch<Client[]>('/api/admin/clients'),

  inboundAddresses: (clientId: string) =>
    apiFetch<InboundAddress[]>(
      `/api/admin/clients/${encodeURIComponent(clientId)}/inbound-addresses`,
    ),
  createInboundAddress: (clientId: string, input: { deploymentId: string; localpart?: string }) =>
    apiFetch<InboundAddress>(
      `/api/admin/clients/${encodeURIComponent(clientId)}/inbound-addresses`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  setInboundAddressActive: (clientId: string, id: string, active: boolean) =>
    apiFetch<{ active: boolean }>(
      `/api/admin/clients/${encodeURIComponent(clientId)}/inbound-addresses/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ active }) },
    ),

  createClient: (input: CreateClientInput) =>
    apiFetch<Client>('/api/admin/clients', { method: 'POST', body: JSON.stringify(input) }),
  getClient: (id: string) => apiFetch<ClientDetail>(`/api/admin/clients/${encodeURIComponent(id)}`),
  updateClient: (id: string, input: UpdateClientInput) =>
    apiFetch<ClientDetail>(`/api/admin/clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  listBlocks: () => apiFetch<Block[]>('/api/admin/blocks'),
  // Deployments are a flat resource server-side; a client's own deployments
  // come back embedded in its detail response rather than from a nested list.
  deployBlock: (clientId: string, input: DeployBlockInput) =>
    apiFetch<Deployment>('/api/admin/deployments', {
      method: 'POST',
      body: JSON.stringify({ clientId, ...input }),
    }),
  getDeployment: (id: string) =>
    apiFetch<Deployment>(`/api/admin/deployments/${encodeURIComponent(id)}`),
  updateDeploymentConfig: (id: string, config: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>(`/api/admin/deployments/${encodeURIComponent(id)}/config`, {
      method: 'PATCH',
      body: JSON.stringify({ config }),
    }),
  setDeploymentEnabled: (id: string, enabled: boolean) =>
    apiFetch<{ status: string }>(
      `/api/admin/deployments/${encodeURIComponent(id)}/${enabled ? 'enable' : 'disable'}`,
      { method: 'POST' },
    ),
  testRunDeployment: (id: string, payload: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/admin/deployments/${encodeURIComponent(id)}/test-run`, {
      method: 'POST',
      body: JSON.stringify({ payload }),
    }),
  deleteDeployment: (id: string) =>
    apiFetch<{ ok: boolean }>(`/api/admin/deployments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  deploymentRuns: (id: string) =>
    apiFetch<unknown[]>(`/api/admin/deployments/${encodeURIComponent(id)}/runs`),
  systemHealth: () => apiFetch<HealthReport>('/api/admin/system/health'),
  checkTemplates: () =>
    apiFetch<unknown>('/api/admin/system/check-templates', { method: 'POST' }),
  ledgerOverview: () => apiFetch<OverviewRow[]>('/api/admin/ledger/overview'),
  ledgerClientThreads: (
    clientId: string,
    params?: {
      blockId?: string | undefined;
      needsAttention?: boolean | undefined;
      sinceDays?: number | undefined;
      limit?: number | undefined;
    },
  ) =>
    apiFetch<LedgerThread[]>(
      `/api/admin/ledger/clients/${encodeURIComponent(clientId)}/threads${toQuery(params ?? {})}`,
    ),
  ledgerLog: (params?: { clientId?: string | undefined; limit?: number | undefined }) =>
    apiFetch<LedgerEventWithClient[]>(`/api/admin/ledger/log${toQuery(params ?? {})}`),
  ledgerDeliveries: (params?: { limit?: number | undefined }) =>
    apiFetch<LedgerDelivery[]>(`/api/admin/ledger/deliveries${toQuery(params ?? {})}`),
  ledgerApprovals: () => apiFetch<LedgerEventWithClient[]>('/api/admin/ledger/approvals'),
  decideApproval: (eventId: string, decision: 'approve' | 'hold') =>
    apiFetch<{ decision: string; resumed: boolean }>(`/api/admin/ledger/approvals/${encodeURIComponent(eventId)}`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),
};

export const clientApi = {
  me: () => apiFetch<ClientMe>('/api/client/me'),
  deployments: () => apiFetch<Deployment[]>('/api/client/deployments'),
  outcomesSummary: () => apiFetch<OutcomesSummary>('/api/client/outcomes/summary'),
  outcomeEvents: () => apiFetch<unknown[]>('/api/client/outcomes/events'),
};

export const demoApi = {
  scenarios: () => publicApiFetch<DemoBlock[]>('/api/demo/scenarios'),
  startRun: (input: StartDemoRunInput) =>
    publicApiFetch<DemoRun>('/api/demo/runs', { method: 'POST', body: JSON.stringify(input) }),
  pollRun: (id: string) => publicApiFetch<DemoRun>(`/api/demo/runs/${encodeURIComponent(id)}`),
};
